// packages/studio/web/src/proposals/ChangeSetSwitcher.tsx
//
// Editor toolbar picker: switch between your in-flight changesets (draft +
// proposed) or jump to the full changes list. You always start from Main, so
// there's no "new change" action. Floating menu, state-badged.

import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItems, MenuItem } from "../ui/Menu";
import { cx } from "../lib/classNames";
import { StateBadge } from "./StateBadge";
import { useMe } from "../api/hooks/useMe";
import { listChangesets, type Changeset } from "./proposalsApi";
import { changeset as changesetPath, repoHome, repoProposals } from "../lib/paths";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";

type Translate = ReturnType<typeof useTranslation>["t"];

export interface ChangeSetSwitcherProps {
  readonly slug: string;
  readonly repo: string;
  readonly active: Changeset;
  /** Whether the active changeset has uncommitted edits (affects its status). */
  readonly hasChanges?: boolean;
}

// The unnamed draft IS trunk — always labelled "Main", with an edited / up-to-
// date status instead of a state badge. Named / proposed changesets use their
// title + state badge.
function isMain(cs: Changeset): boolean {
  return cs.state === "draft" && !cs.title.trim();
}
function name(cs: Changeset, t: Translate): string {
  return isMain(cs) ? t("switcher.main") : cs.title.trim() || t("switcher.untitled");
}

// Status pill for "Main" — matches StateBadge visuals (rounded-full, dot, ring).
function MainStatus({ edited }: { readonly edited: boolean }) {
  const { t } = useTranslation("proposals");
  const s = edited
    ? { dot: "bg-amber-400", chip: "bg-amber-500/15 text-amber-200 ring-amber-500/30", label: t("switcher.edited") }
    : { dot: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30", label: t("switcher.upToDate") };
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        s.chip,
      )}
    >
      <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} aria-hidden="true" />
      {s.label}
    </span>
  );
}

export function ChangeSetSwitcher({ slug, repo, active, hasChanges = true }: ChangeSetSwitcherProps) {
  const { t } = useTranslation("proposals");
  const navigate = useNavigate();
  const menu = useContextMenu();
  const meId = useMe().data?.id ?? null;

  const { data } = useQuery({
    queryKey: ["my-changesets", slug, repo],
    queryFn: async () => {
      // My drafts (per-user) + every open change in the repo (mine + others').
      const [drafts, proposed] = await Promise.all([
        listChangesets(slug, repo, { mine: true, state: "draft" }),
        listChangesets(slug, repo, { state: "proposed" }),
      ]);
      return [...drafts, ...proposed];
    },
  });

  const list = data ?? [active];
  const withActive = list.some((c) => c.id === active.id) ? list : [active, ...list];
  // Main (the unnamed draft) always pinned to the top.
  const options = [...withActive].sort((a, b) => Number(isMain(b)) - Number(isMain(a)));

  function openCs(cs: Changeset) {
    navigate(changesetPath(slug, repo, cs.number));
  }
  function showAll() {
    navigate(repoProposals(slug, repo));
  }
  function itemsFor(cs: Changeset): ContextMenuItem[] {
    const path = changesetPath(slug, repo, cs.number);
    return [
      { label: t("menu.open"), onClick: () => openCs(cs) },
      {
        label: t("menu.copyLink"),
        onClick: () => void navigator.clipboard?.writeText(`${location.origin}${path}`),
      },
    ];
  }

  // Main = trunk. It's the unnamed draft when one exists (with live edited
  // status), but it must ALWAYS be offered: naming the unnamed draft turns it
  // into a real changeset, after which jumping to Main get-or-creates a fresh
  // one (navigate to trunk with no ?cs).
  const mainDraft = options.find(isMain) ?? null;
  const isMine = (c: Changeset) => meId != null && c.authorUserId === meId;
  const named = options.filter((c) => !isMain(c));
  const mineNamed = named.filter(isMine);
  const otherNamed = named.filter((c) => !isMine(c));
  const onMain = isMain(active);
  function openMain() {
    if (mainDraft) openCs(mainDraft);
    else navigate(repoHome(slug, repo));
  }

  const renderRow = (cs: Changeset) => (
    <div key={cs.id} onContextMenu={(e) => menu.open(e, itemsFor(cs))}>
    <MenuItem
      onClick={() => openCs(cs)}
      className={cx(
        "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none",
        cs.id === active.id ? "bg-slate-800/60 text-slate-100" : "text-slate-300",
      )}
    >
      <StateBadge state={cs.state} mine={isMine(cs)} />
      <span className="truncate">{name(cs, t)}</span>
      {cs.id === active.id && (
        <svg className="ml-auto h-4 w-4 shrink-0 text-arch-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
    </MenuItem>
    </div>
  );

  return (
    <>
    <Menu>
      <MenuButton
        // Accessible name = generic label + the current changeset name, so a
        // screen reader announces WHICH change is active (the visible name is
        // otherwise the accessible name; the shared MenuButton forwards aria-label).
        aria-label={`${t("switcher.activeAria")}: ${name(active, t)}`}
        className="flex max-w-60 items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
      >
        {isMain(active) ? <MainStatus edited={hasChanges} /> : <StateBadge state={active.state} mine={isMine(active)} />}
        <span className="truncate">{name(active, t)}</span>
        <svg className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </MenuButton>
      <MenuItems>
        <div className="max-h-72 overflow-y-auto">
          {/* Main — always present. */}
          <div
            onContextMenu={(e) =>
              menu.open(e, [
                { label: t("menu.open"), onClick: openMain },
                ...(mainDraft ? itemsFor(mainDraft).slice(1) : []),
              ])
            }
          >
          <MenuItem
            onClick={openMain}
            className={cx(
              "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none",
              onMain ? "bg-slate-800/60 text-slate-100" : "text-slate-300",
            )}
          >
            <MainStatus edited={onMain ? hasChanges : false} />
            <span className="truncate">{t("switcher.main")}</span>
            {onMain && (
              <svg className="ml-auto h-4 w-4 shrink-0 text-arch-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </MenuItem>
          </div>
          {mineNamed.map(renderRow)}
          {otherNamed.length > 0 && (
            <>
              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                {t("switcher.openByOthers")}
              </p>
              {otherNamed.map(renderRow)}
            </>
          )}
        </div>
        <div className="my-1 h-px bg-slate-800" />
        <MenuItem
          onClick={showAll}
          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-slate-300 hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {t("switcher.showAll")}
        </MenuItem>
      </MenuItems>
    </Menu>
    {menu.node}
    </>
  );
}
